import { JsonRPCService } from '@xengage/gw-portals-transport-js';
import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';

/**
 * Invokes the given method on the backend endpoint passing the given data as parameters
 *
 * @param {any} method the method to invoke on the backend endpoint
 * @param {any} data the data to be passed as part of the invokation on the backend
 * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
 * @returns {Promise} the promise from the backend call
 */
function processSubmission(method, data, additionalHeaders = {}) {
    return JsonRPCService.send(
        getProxiedServiceUrl('gatewayquote/quote'),
        method,
        data,
        additionalHeaders
    );
}

function ensureIsSetToDraft(submissionDTO) {
    if (isSubmissionQuoted(submissionDTO)) {
        return setQuoteToDraft(submissionDTO.quoteID, submissionDTO.sessionUUID);
    }
    return Promise.resolve(submissionDTO);
}

function setQuoteToDraft(quoteID, sessionUUID) {
    return new Promise(function(resolve, reject) {
        JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'), 'setQuoteToDraft', [quoteID, sessionUUID]).then(
            (response) => {
                const submissionDTO = new Submission(response);
                resolve(submissionDTO);
            },
            reject()
        );
    })
}

function compareSubmissionFields(mainDTO, responseDTO, path) {
    const mainField = _.get(mainDTO, path);
    const responseField = _.get(responseDTO, path);
    if (!_.isNil(mainField) && !_.isEqual(mainField, responseField)){
        _.set(responseDTO, path, mainField);
    }
}

function isSubmissionQuoted(submissionDTO) {
    return submissionDTO.quoteData && _.some(submissionDTO.quoteData.offeredQuotes, quote => quote.status === 'Quoted');
}

/**
 * This is the server responsible for dealing with the Quote and Buy backend operations
 *
 * @export
 * @class LoadSaveService
 */
export default class LoadSaveService {
    /**
     * Updates, saves and performs a quote on an existing submission.
     * Generates quotes for all available product offerings.
     *
     * @param {Object} data tge submission that will be saved
     * @returns {Promise} the promise from the backend call
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @memberof LoadSaveService
     */
    static saveAndQuoteSubmission(data, additionalHeaders = {}) {
        return processSubmission('saveAndQuote', [data], additionalHeaders);
    }

    /**
     * Retrieves a submission.
     *
     * @param {Object} data the retrieval payload (QuoteRetrievalDTO)
     * @returns {Promise} the promise from the backend call
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @memberof LoadSaveService
     */
    static retrieveSubmission(data, additionalHeaders) {
        return processSubmission('retrieve', [data], additionalHeaders)
    }

    /**
     * Updates a quoted submission with LOB Data.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateQuotedSubmissionWithLOBData(data, additionalHeaders = {}) {
        return processSubmission(
            'updateQuotedSubmissionWithLOBData',
            [data],
            additionalHeaders
        );
    }

    /**
     * Retrieves a submission.
     *
     * @param {string} quoteID quoteID of submission that needs to be found
     * @param {string} sessionUUID current session id
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static retrievePaymentPlans(quoteID, sessionUUID, additionalHeaders = {}) {
        return processSubmission(
            'retrievePaymentPlans',
            [quoteID, sessionUUID],
            additionalHeaders
        );
    }

    /**
     * Binds the submission.
     *
     * @param {Object} data the submission to be bound
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static bindSubmission(data, additionalHeaders = {}) {
        return processSubmission('bind', [data], additionalHeaders);
    }

    /**
     * Updates an existing draft submission.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateDraftSubmission(data, additionalHeaders = {}) {
        return processSubmission(
            'updateDraftSubmission',
            [data],
            additionalHeaders
        );
    }

    /**
     * Updates a quoted submission.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateQuotedSubmission(data, additionalHeaders = {}) {
        return processSubmission(
            'updateQuotedSubmission',
            [data],
            additionalHeaders
        );
    }

    /**
     * Updates coverages.
     *
     * @param {Object} quoteID quoteID
     * @param {Object} sessionUUID sessionUUID
     * @param {Object} lobCoverages the coverages to be saved
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */

    static updateCoverages(
        quoteID,
        sessionUUID,
        lobCoverages,
        additionalHeaders = {}
    ) {
        return processSubmission(
            'updateCoverages',
            [quoteID, sessionUUID, lobCoverages],
            additionalHeaders
        );
    }

    /**
     * Approve Underwriting Issue.
     *
     * @param {Object} quoteID quoteID
     * @param {Object} uwIssue uwIssue
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static approveUnderwritingIssue(quoteID, uwIssue, additionalHeaders = {}) {
        return processSubmission(
            'approveUnderwritingIssue',
            [quoteID, uwIssue],
            additionalHeaders
        );
    }
      /**
     * Changes policyType and updates draft submission to obtain qualification questions for specific policy type.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */

    static changePolicyTypeAndUpdateDraftSubmission_TWIA(data, additionalHeaders) {
        return processSubmission(
            'changePolicyTypeAndUpdateDraftSubmission_TWIA',
            [data],
            additionalHeaders
        );
    }

          /**
     * Changes policyType and updates draft submission to obtain qualification questions for specific policy type.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */

    static validatePreQualAndUpdateDraftSubmission_TWIA(data, additionalHeaders) {
        return processSubmission(
            'validatePreQualAndUpdateDraftSubmission_TWIA',
            [data],
            additionalHeaders
        );
    }

    static saveSubmission(data, additionalHeaders) {
        return ensureIsSetToDraft(data)
                    .then(respSubmissionDTO => {
                        compareSubmissionFields(data, respSubmissionDTO, 'baseData.periodStartDate'); 
                        compareSubmissionFields(data, respSubmissionDTO, 'baseData.accountHolder.firstName'); 
                        compareSubmissionFields(data, respSubmissionDTO, 'baseData.accountHolder.lastName'); 
                        compareSubmissionFields(data, respSubmissionDTO, 'baseData.accountHolder.contactName'); 
                        return processSubmission(
                            'updateDraftSubmission',
                            [data],
                            additionalHeaders
                        );
                    });
    }

    // US26334
    static updatePropertyDetailsAndSubmission(data, additionalHeaders) {
        return processSubmission(
            // eslint-disable-next-line no-secrets/no-secrets
            'updateDraftSubmissionAndPerformValuation_TWIA',
            [data],
            additionalHeaders
        );
    }

    // US26292 and US26874
    static updateQuotedSubmissionWithoutLOB_TWIA(data, additionalHeaders) {
        return processSubmission(
            // eslint-disable-next-line no-secrets/no-secrets
            'updateQuotedSubmissionWithoutLOB_TWIA',
            [data],
            additionalHeaders
        );
    }

    static retrieveBindDataWithPaymentPlans_TWIA(quoteID, sessionID, additionalHeaders) {
        return processSubmission(
            // eslint-disable-next-line no-secrets/no-secrets
            'retrieveBindDataWithPaymentPlans_TWIA',
            [quoteID, sessionID],
            additionalHeaders
        );
    }

    static submitOrBind_TWIA(quoteData, additionalHeaders) {
        return processSubmission(
            'submitOrBind_TWIA',
            [quoteData],
            additionalHeaders
        );
    }

    static updateAndForgetDraftSubmission(data, additionalHeaders = {}) {
        return processSubmission(
            'updateAndForgetDraftSubmission_TWIA',
            [data],
            additionalHeaders
        );
    }

    static updateAndForgetQuotedSubmission(data, additionalHeaders = {}) {
        return processSubmission(
            'updateAndForgetQuotedSubmission_TWIA',
            [data],
            additionalHeaders
        );
    }

    static retrievePaymentPlansBySubmission(data, additionalHeaders = {}) {
        return processSubmission(
            'retrievePaymentPlansBySubmission',
            [data],
            additionalHeaders
        );
    }

}
